<template>
  <div
    aria-label="The audio player for snnippets"
    :class="[ 'audioContainer', { hidden: location == 'map' && !audio } ]"
    tabindex="0"
  >

    <Captions
      :cues="cues"
      @select_track="$emit( 'select_track', $event )"
    />

    <audio
      :autoplay="location == 'meditation'"
      ref="audio"
      preload="metadata"
      crossorigin="anonymous"
      :muted="muted"
      @loadedmetadata="handleLoaded()"
      @timeupdate="$emit('elapsed', $event.target.currentTime / $event.target.duration)"
      @ended="$emit('ended')"
    >
      <source :src="audioURL" />
      <track
        default
        srclang="en"
        kind="captions"
        label="English subtitles"
        ref="track"
        :src="trackSRC"
        @cuechange="handleCueChange"
      />
    </audio>

    <div
      v-if="location == 'map'"
      class="controls"
    >
      <Button
        @click="$emit('prev')"
        @keydown.space="$emit('prev')"
        @keydown.enter="$emit('prev')"
        icon="prev"
        label="previous track"
      />
      <Button
        v-if="loading"
        icon="loading"
        label="loading track"
      />
      <Button
        v-else-if="playing"
        @click="pause"
        @keydown.space="pause"
        @keydown.enter="pause"
        icon="pause"
        label="pause playback"
      />
      <Button
        v-else
        @click="play"
        @keydown.space="play"
        @keydown.enter="play"
        icon="play"
        label="resume playback"
      />
      <Button
        @click="$emit('next')"
        @keydown.space="$emit('next')"
        @keydown.enter="$emit('next')"
        icon="next"
        label="next track"
      />
    </div>

    <div
      v-else-if="location == 'meditation'"
      class="controls"
    >
      <Button
        v-if="loading || !audio"
        icon="loading"
        label="stream is idle, waiting for next in line"
      />
      <Button
        v-else-if="muted"
        @click="unmute"
        @keydown.space="unmute"
        @keydown.enter="unmute"
        icon="mute"
        label="unmute player"
      />
      <Button
        v-else
        @click="mute"
        @keydown.space="mute"
        @keydown.enter="mute"
        icon="volume"
        label="mute player"

      />
      <Button
        @click="reload"
        @keydown.space="reload"
        @keydown.enter="reload"
        icon="reload"
        label="reload the webpage"
      />
      <p class="inst"> <em>Press "↻" to rejoin. </em></p>
    </div>



  </div>
</template>

<script>
import { mapState } from 'vuex'
import api from '../../../api'
import { captions } from '../../../utils'
import Button from '../Button'
import Captions from './Captions'

export default {

  name: "Audio",

  props: [
    'audio',
    'track',
    'past_phrases'
  ],

  computed: {
    audioURL() { return this.audio && this.audio.audio && this.$apiURL + this.audio.audio.url },
    trackURL() { return this.audio && this.audio.captions && this.$apiURL + this.audio.captions.url },
    ...mapState([
      'location',
    ]),
    ...mapState('access', [
      'interacted',
      'options'
    ]),
    muted() {
      return !( this.interacted && this.options.sound.value )
    },
  },

  emits: [
    'elapsed',
    'prev',
    'next',
    'ended',
    'select_track'
  ],

  components: {
    Button,
    Captions
  },

  data() {
    return {
      duration: null,
      loading: true,
      trackSRC: null,
      cues: []
    }
  },

  async created() {
    await this.get_past_cues()
    this.trackSRC = this.trackURL && await this.loadTrack(this.trackURL)
  },

  mounted() {
    // this.$el.focus()
    this.load()
    this.play()
  },

  watch: {
    async audio() {
      if ( this.audio && this.audio.audio ) {
        // this.$el.focus()
        this.loading = true
        if ( this.location == "map" ) {
          this.cues = []
        }
        this.trackSRC = this.trackURL && await this.loadTrack(this.trackURL)
        this.load()
        this.play()
      } else {
        this.trackSRC = null
        this.pause()
      }
    },
  },

  methods: {

    ...captions,

    load() {
      this.$refs.audio.load()
    },

    handleLoaded() {
      this.loading = false
    },

    play() {
      this.$refs.audio.play()
      this.playing = true
    },

    pause() {
      this.$refs.audio.pause()
      this.playing = false
    },

    mute() {
      this.$store.dispatch( 'access/setOption', {
        key: 'sound',
        value: false
      })
    },

    unmute() {
      this.$store.dispatch( 'access/setOption', {
        key: 'sound',
        value: true
      })
    },

    reload() {
      window.location.reload( true )
    },

    handleCueChange() {
      if (this.$refs.track) {
        const cue = this.$refs.track.track.activeCues[0]
        if (cue && cue.text) {
          const cuearray = cue.text.split(' ')
          let previousDelay = 0
          for (let l = 0; l < cuearray.length; l ++) {
            let
              currentDelay = 2 * l * this.duration / cuearray.length,
              sum = previousDelay + currentDelay
              previousDelay = currentDelay
            setTimeout(() => {
              this.cues.push({
                text     : cuearray[l] + ' ',
                r        : Math.random() * (5 - -5) -5,
                x        : Math.random() * (10 - -10) -10,
                track_id : this.track?.id
              })
            }, sum)
          }
        }
      }
    },

    async get_past_cues() {
      if ( this.past_phrases && this.past_phrases.length ) {
        for ( const phrase of this.past_phrases ) {
          const { audio } = phrase
          if ( audio.captions ) {
            const trackURL = this.$apiURL + audio.captions.url
            const track = await this.loadTrack( trackURL )
            const { data } = await api.get( track )
            const cues = []
            this.parse_vtt( data ).map( cue =>
              cue.split( " " ).map( text =>  {
                cues.push({
                  text     : text + ' ',
                  r        : Math.random() * (5 - -5) -5,
                  x        : Math.random() * (10 - -10) -10,
                  track_id : phrase.id,
                  old      : true
                })
              })
            )
            this.cues.push( ...cues )
            // await this.delay( 50 )
          }
        }
      }
    },

    async delay( t ) {
      return new Promise( resolve => setTimeout( resolve, t ) )
    }

  },



}
</script>

<style scoped>
.audioContainer {
  box-sizing: border-box;
  position: absolute;
  bottom: 0;
  right: 2em;
  width: 20em;
  z-index: 600;
  padding: 3em 0em;
  transition: all var(--slow) ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}
.audioContainer.hidden {
  transform: scale(0.7) translateY(1000px);
  transform-origin: bottom center;
  opacity: 0;
}

.audioContainer .controls {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0 auto;
  z-index: 1;
}

.audioContainer .controls .inst {
  display: none;
}

@media screen and (max-width : 1100px)  and (min-width : 750px) {
/* @media screen and (max-width : 1100px) { */
  .audioContainer .controls .inst {
    display: inline;
    position: absolute;
    bottom: 0;
    right: 50px;
  }

  .audioContainer >>> .captions {
    padding-top: 2em;
  }

}

.isMobile .audioContainer {
  width: 100%;
  height: 10vh;
  right: 0;
  padding: 1.25em;
  align-items: flex-end;
  justify-content: flex-end;
}

.isMobile .audioContainer .controls {
  justify-content: flex-end;
  padding: 0;
}

</style>
